import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import Pagination from '../../../components/Pagination';
import IncidentCreateModal from './IncidentCreateModal';
import * as IncidentInfoModalUI from './IncidentInfoModalUI';
import moment from 'moment';
import { IIncident } from '../../../pages/Incidents/interfaces';
import { Callout, DirectionalHint } from '@fluentui/react';
import TimezonePopup from '../../TimezonePopup';
import { IncidentType } from '../../IncidentType';
import { ModalSliceType, openModal } from '../../../redux/slices/modal';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectOpenedModalType } from '../../../redux/selectors/modal';
import { parseIncidentDescription } from '../utils';
import { isNil } from 'lodash';
import { globalFormat } from '../../../utils/formatting';
import { GraphQLDate } from '../../../utils/types';
import KubitTooltip from '../../KubitTooltip';
import { SvgIcon } from '../../Icon';

interface IProps {
  currentIncident: IIncident;
  incidents?: IIncident[];
  activeIndex?: number;
  setActiveIndex?: (newIndex: number) => void;
  closeParentPopup: () => void;
  setIsEditOpen?: () => void;
  setIsEditClose?: () => void;
  setActivePage?: (page: number) => void;
  analysisId?: string;
  stopMovementPropagation?: boolean;
  dismiss: () => void;
}

export default memo(function IncidentInfoModal({
  currentIncident,
  incidents,
  activeIndex,
  setActiveIndex,
  closeParentPopup,
  setIsEditOpen,
  setIsEditClose,
  analysisId,
  stopMovementPropagation,
  dismiss,
}: IProps) {
  const { dateFormat } = globalFormat.getGlobalFormat();
  const dispatch = useAppDispatch();
  const triggerReference = useRef<any>();

  const isDeleteModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.IncidentDelete, ''),
  );

  const { start, end, name, type } = currentIncident;
  const description = parseIncidentDescription(currentIncident?.description);
  const incidentSelectedStartDate = useMemo(
    () => moment(start).toDate(),
    [start],
  );
  const incidentSelectedEndDate = useMemo(() => moment(end).toDate(), [end]);
  const shouldRenderPagination =
    incidents && incidents.length > 1 && !isNil(activeIndex);

  const onIncidentsIndexChange = (activePage: number) => {
    setActiveIndex?.(activePage - 1);
  };

  const [isEditPopupOpen, setEditPopupOpen] = useState(false);

  const onEditPopupOpen = () => {
    setEditPopupOpen(true);
    if (setIsEditOpen) {
      setIsEditOpen();
    }
  };
  const onEditPopupClose = () => {
    setEditPopupOpen(false);
    if (setIsEditClose) {
      setIsEditClose();
    }
  };

  const handleCloseCreateModal = () => {
    onEditPopupClose();

    if (closeParentPopup) {
      closeParentPopup();
    }
  };

  const isSingleDayEvent = useMemo(
    () => moment(start).isSame(moment(end), 'day'),
    [start, end],
  );
  const parseDate = useCallback(
    (newDate: GraphQLDate) => (
      <TimezonePopup timestamp={newDate} dateFormat={dateFormat} />
    ),
    [dateFormat],
  );
  const parsedDate = useMemo(
    () =>
      isSingleDayEvent ? (
        parseDate(start)
      ) : (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {parseDate(start)}
          <span style={{ margin: '0px 4px' }}>~</span>
          {parseDate(end)}
        </div>
      ),
    [parseDate, isSingleDayEvent, start, end],
  );

  const openDeleteModal = useCallback(() => {
    dispatch(
      openModal({
        id: analysisId,
        type: ModalSliceType.IncidentDelete,
        data: {
          incident: currentIncident,
          analysisId,
        },
      }),
    );
    dismiss();
  }, [dispatch, dismiss, currentIncident, analysisId]);

  const onMouseMoveInContainer = useCallback(
    (mouseEvent: any) =>
      stopMovementPropagation ? mouseEvent.stopPropagation() : null,
    [stopMovementPropagation],
  );

  return (
    <IncidentInfoModalUI.Container onMouseMove={onMouseMoveInContainer}>
      <IncidentInfoModalUI.Header>
        <IncidentInfoModalUI.DateWrapper>
          {parsedDate}
        </IncidentInfoModalUI.DateWrapper>
        {isEditPopupOpen && (
          <Callout
            target={triggerReference}
            directionalHint={DirectionalHint.rightCenter}
            onDismiss={onEditPopupClose}
          >
            <IncidentCreateModal
              currentIncident={currentIncident}
              selectedStartDate={incidentSelectedStartDate}
              selectedEndDate={incidentSelectedEndDate}
              analysisId={analysisId}
              onClose={handleCloseCreateModal}
              closeOnScroll
            />
          </Callout>
        )}
        <KubitTooltip
          content={'Edit this incident'}
          placement="bottom-start"
          noOpenDelay={true}
        >
          <SvgIcon
            ref={triggerReference}
            name="pencil"
            width={16}
            height={16}
            onClick={onEditPopupOpen}
            isActive={isEditPopupOpen}
            isHoverable={true}
          />
        </KubitTooltip>
        <KubitTooltip
          content={'Delete this incident'}
          placement="bottom-start"
          noOpenDelay={true}
        >
          <SvgIcon
            name="trash"
            width={16}
            height={16}
            onClick={openDeleteModal}
            isActive={isDeleteModalOpen}
            isHoverable={true}
          />
        </KubitTooltip>
      </IncidentInfoModalUI.Header>
      <IncidentInfoModalUI.Body>
        <IncidentInfoModalUI.Title>{name}</IncidentInfoModalUI.Title>
        {description && (
          <IncidentInfoModalUI.Description>
            {description}
          </IncidentInfoModalUI.Description>
        )}
        <IncidentInfoModalUI.Footer>
          <IncidentType name={type.name} />
          {shouldRenderPagination && (
            <Pagination
              totalPages={incidents.length}
              activePage={activeIndex + 1}
              onPageChange={onIncidentsIndexChange}
            />
          )}
        </IncidentInfoModalUI.Footer>
      </IncidentInfoModalUI.Body>
    </IncidentInfoModalUI.Container>
  );
});
